import { fetchAPI } from './fetchAPI';

// ==================== Prompts ====================

export const getPrompts = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
  if (params.promptType) queryParams.append('promptType', params.promptType);
  if (params.status) queryParams.append('status', params.status);
  if (params.environment) queryParams.append('environment', params.environment);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/prompts${query ? '?' + query : ''}`);
};

export const listEnvironments = async (): Promise<any> => {
  return fetchAPI('/genaiops/prompts/environments');
};

export const getPrompt = async (promptName: string, version?: number): Promise<any> => {
  const query = version ? `?version=${version}` : '';
  return fetchAPI(`/genaiops/prompts/${promptName}${query}`);
};

export const createPrompt = async (promptData: Record<string, any>): Promise<any> => {
  return fetchAPI('/genaiops/prompts', {
    method: 'POST',
    body: JSON.stringify(promptData),
  });
};

// NOTE: Content-Type header is automatically added by fetchAPI

export const updatePrompt = async (promptId: string, updates: Record<string, any>): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });
};

export const deletePrompt = async (promptId: string): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}`, {
    method: 'DELETE',
  });
};

export const testPrompt = async (promptId: string, variables: Record<string, any> = {}, modelConfig: Record<string, any> | null = null): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      variables,
      config: modelConfig,
    }),
  });
};

export const executePrompt = async (promptId: string, options: Record<string, any> = {}): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/execute`, {
    method: 'POST',
    body: JSON.stringify({
      variables: options.variables || {},
      model_id: options.model_id,
      max_tokens: options.max_tokens || 1024,
      temperature: options.temperature || 0.7,
      save_to_history: options.save_to_history !== false,
    }),
  });
};

export const getPromptAnalytics = async (promptId: string, days: number = 30): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/analytics?days=${days}`);
};

export const bulkPromptOperation = async (promptIds: string[], operation: string): Promise<any> => {
  return fetchAPI('/genaiops/prompts/bulk', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt_ids: promptIds,
      operation,
    }),
  });
};

export const importPrompts = async (promptsData: Record<string, unknown>[], conflictResolution: string = 'skip'): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/import?conflict_resolution=${conflictResolution}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(promptsData),
  });
};

export const clonePrompt = async (promptId: string, newName: string | null = null, bedrockSync: boolean = true): Promise<any> => {
  if (!newName) {
    // Fallback: get original prompt name and append -copy
    const prompt = await fetchAPI(`/genaiops/prompts/${promptId}`) as { name: string };
    newName = `${prompt.name}-copy`;
  }

  return fetchAPI(`/genaiops/prompts/${promptId}/clone`, {
    method: 'POST',
    body: JSON.stringify({
      new_name: newName,
      bedrock_sync: bedrockSync,
    }),
  });
};

export const getPromptVersions = async (promptId: string, environment: string | null = null): Promise<any> => {
  const query = environment ? `?environment=${environment}` : '';
  return fetchAPI(`/genaiops/prompts/${promptId}/versions${query}`);
};

export const promotePrompt = async (promptId: string, targetEnvironment: string, releaseNotes: string | null = null): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/promote`, {
    method: 'POST',
    body: JSON.stringify({
      target_environment: targetEnvironment,
      release_notes: releaseNotes,
    }),
  });
};

export const rollbackPrompt = async (promptId: string, targetVersion: number): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({
      target_version: targetVersion,
    }),
  });
};

export const assignVersionLabel = async (promptId: string, label: string): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/labels`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
};

export const removeVersionLabel = async (promptId: string, label: string): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/labels/${label}`, {
    method: 'DELETE',
  });
};

export const getPromptLabels = async (promptId: string): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/labels`);
};

export const resolvePrompt = async (name: string, environment: string = 'production', label: string = 'stable'): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/resolve?name=${encodeURIComponent(name)}&environment=${encodeURIComponent(environment)}&label=${encodeURIComponent(label)}`);
};

export const renamePrompt = async (promptId: string, newName: string): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}/rename`, {
    method: 'POST',
    body: JSON.stringify({ new_name: newName }),
  });
};

// ==================== Environment Management ====================

export const getEnvironments = async (): Promise<any> => {
  return fetchAPI('/genaiops/environments');
};

export const createEnvironment = async (data: Record<string, any>): Promise<any> => {
  return fetchAPI('/genaiops/environments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateEnvironment = async (environmentId: string, data: Record<string, any>): Promise<any> => {
  return fetchAPI(`/genaiops/environments/${environmentId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

export const deleteEnvironment = async (environmentId: string): Promise<any> => {
  return fetchAPI(`/genaiops/environments/${environmentId}`, {
    method: 'DELETE',
  });
};

export const reorderEnvironments = async (order: Record<string, unknown>[]): Promise<any> => {
  return fetchAPI('/genaiops/environments/reorder', {
    method: 'PUT',
    body: JSON.stringify({ order }),
  });
};

export const assignPromptProject = async (promptId: string, projectId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}`, {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId }),
  });
};

export const assignPromptWorkload = async (promptId: string, workloadId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/prompts/${promptId}`, {
    method: 'PATCH',
    body: JSON.stringify({ workload_id: workloadId }),
  });
};
